import PropTypes from 'prop-types';
import { forwardRef, useState, useRef, useImperativeHandle, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import styles from './FJTextAreaTextToImage.module.less';
import InspireIcon from '../../../../assets/svg/inspire-me.svg?react';
import AiPromptIcon from '../../../../assets/svg/ai-prompt.svg?react';
import FJDropDown from '../../../../common/components/FJDropDown/FJDropDown';
import AIPromptOptimizeIcon from '../../../../assets/svg/ai-prompt-optimize.svg?react';
import UseImageAsPromptIcon from '../../../../assets/svg/use-image-as-prompt.svg?react';
import FJLocalStore from '../../../../store/FJLocalStore';

const FJTextAreaTextToImage = forwardRef((props, ref) => {
    const {
        text,
        onBlur,
        disabled,
        maxLength,
        onChange,
        onKeyDown,
        readOnly,
        placeholder = 'Describe the image you want to generate',
        className,
        inputClassName,
        onFocus,

        showUploadImageAsPromptModal,
        getInspire,
        aiOptimizing,
        aiPromptDisabled,
    } = props;

    const [stateText, setStateText] = useState(text || '');
    const [isFocused, setIsFocused] = useState(false);
    const textareaRef = useRef(null);
    const aiPromptDropDownRef = useRef(null);

    useImperativeHandle(ref, () => ({
        focus: () => {
            if (textareaRef.current) {
                textareaRef.current.focus();
            }
        },
        setText: text => {
            setStateText(text);
        },
        getTextarea: () => {
            return textareaRef.current;
        },
    }));

    useEffect(() => {
        if (text || text === '') {
            setStateText(text);
        }
    }, [text]);

    const _onBlur = e => {
        setIsFocused(false);
        if (onBlur) {
            onBlur(e);
        }
    };

    const _onFocus = e => {
        setIsFocused(true);
        if (onFocus) {
            onFocus(e);
        }
    };

    const _onKeyDown = useCallback(
        e => {
            if ((e.ctrlKey || e.metaKey) && e.keyCode === 65) {
                const value = textareaRef.current.value;
                textareaRef.current.value = '';
                textareaRef.current.value = value;
                textareaRef.current.select();
            }

            if (onKeyDown) {
                onKeyDown(e);
            }
        },
        [textareaRef, onKeyDown],
    );

    const _onChange = e => {
        setStateText(e.target.value);
        if (onChange) {
            onChange(e.target.value);
        }
    };

    const _onForbidDrag = e => {
        e.preventDefault();
        e.stopPropagation();
        return;
    };

    const cnPanel = classNames(
        styles.textarea_box,
        isFocused ? styles.textarea_focus : '',
        disabled ? styles.textarea_disabled : '',
        className,
    );
    return (
        <div className={cnPanel}>
            <textarea
                className={classNames(styles.content_textarea, inputClassName)}
                ref={textareaRef}
                readOnly={readOnly}
                value={stateText}
                placeholder={placeholder}
                onBlur={_onBlur}
                onKeyDown={_onKeyDown}
                onChange={_onChange}
                onFocus={_onFocus}
                maxLength={maxLength}
                disabled={disabled}
                onDragStart={_onForbidDrag}
            />
            <div className={styles.bottom_btu_box}>
                <div className={styles.inspire_me} onClick={getInspire}>
                    <InspireIcon />
                    {FJLocalStore._('inspire-me')}
                </div>
                <FJDropDown
                    disabled={aiPromptDisabled}
                    ref={aiPromptDropDownRef}
                    overlay={
                        <div className={styles.ai_prompt_dropdown_box}>
                            {[
                                {
                                    icon: AIPromptOptimizeIcon,
                                    text: FJLocalStore._('improve-prompts'),
                                    onClick: aiOptimizing,
                                },
                                {
                                    icon: UseImageAsPromptIcon,
                                    text: FJLocalStore._('use-image-as-prompt'),
                                    onClick: showUploadImageAsPromptModal,
                                },
                            ].map((item, index) => (
                                <div
                                    key={index}
                                    className={styles.ai_prompt_dropdown_item}
                                    onClick={() => {
                                        item.onClick();
                                        aiPromptDropDownRef.current.hide();
                                    }}
                                >
                                    <item.icon />
                                    <div className={styles.ai_prompt_dropdown_item_text}>{item.text}</div>
                                </div>
                            ))}
                        </div>
                    }
                    triggerDisplay='flex'
                    placement='bottomLeft'
                    isTopLayer={true}
                >
                    <div className={styles.ai_prompt}>
                        <AiPromptIcon />
                        {FJLocalStore._('ai-prompt')}
                    </div>
                </FJDropDown>
            </div>
        </div>
    );
});

FJTextAreaTextToImage.displayName = 'FJTextAreaTextToImage';

FJTextAreaTextToImage.propTypes = {
    text: PropTypes.string,
    onBlur: PropTypes.func,
    disabled: PropTypes.bool,
    maxLength: PropTypes.number,
    onChange: PropTypes.func,
    onKeyDown: PropTypes.func,
    readOnly: PropTypes.bool,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    inputClassName: PropTypes.string,
    onFocus: PropTypes.func,
    showUploadImageAsPromptModal: PropTypes.func,
    getInspire: PropTypes.func,
    aiOptimizing: PropTypes.func,
    aiPromptDisabled: PropTypes.bool,
};

export default FJTextAreaTextToImage;
